// src/api/widgetTemplateService.js
import apiClient from './axiosConfig'; // For calls requiring User JWT

const API_URL = process.env.REACT_APP_API_URL || '';

// Standardized error handler (copied from original widgetService.js)
const handleApiError = (error, operation) => {
  console.error(
    `${error.config?.method?.toUpperCase()} ${error.config?.url} – Error ${operation}:`,
    error
  );
  let message = 'An unknown error occurred.';
  const status = error.response?.status;

  if (error.response?.data) {
    const d = error.response.data;
    if (typeof d.detail === 'string') {
      message = d.detail;
    } else if (Array.isArray(d.detail)) {
      message = d.detail
        .map((e) => {
          const loc = e.loc?.slice(1).join('.') || 'field';
          return `${loc}: ${e.msg}`;
        })
        .join('; ');
      if (!message) message = 'Validation error';
    } else if (typeof d === 'string' && d.length < 500 && !d.trim().startsWith('<')) {
      message = d;
    } else if (d.message && typeof d.message === 'string') {
      message = d.message;
    } else if (status) {
      message = `Request failed with status code ${status}`;
    }
    if (typeof message === 'string' && message.trim().startsWith('<')) {
         message = `Received an HTML error page (status ${status || 'unknown'}) during ${operation}.`;
     }
  } else if (error.request) {
    message = `No response from server during ${operation}. Check network connection.`;
  } else if (error.message) {
    message = error.message;
  }
  return { isError: true, message, status, originalError: error };
};

export const widgetTemplateService = {
  /**
   * GET /api/v1/widgets/templates
   * Lists all widget templates accessible to the user.
   */
  listTemplates: async () => {
    try {
      const { data } = await apiClient.get(
        `${API_URL}/api/v1/widgets/templates`
      );
      return data;
    } catch (err) {
      return handleApiError(err, 'listing widget templates');
    }
  },

  /**
   * GET /api/v1/widgets/templates/{templateId}
   * Fetches details for a specific widget template.
   */
  getTemplate: async (templateId) => {
    if (!templateId) return { isError: true, message: "Template ID required.", status: 400 };
    try {
      const { data } = await apiClient.get(
        `${API_URL}/api/v1/widgets/templates/${templateId}`
      );
      return data;
    } catch (err) {
      return handleApiError(err, `fetching template ${templateId}`);
    }
  },

  /**
   * POST /api/v1/widgets/templates
   * Creates a new widget template.
   */
  createTemplate: async (templateData) => {
    try {
      const { data } = await apiClient.post(
        `${API_URL}/api/v1/widgets/templates`,
        templateData
      );
      return data;
    } catch (err) {
      return handleApiError(err, 'creating widget template');
    }
  },

  /**
   * PUT /api/v1/widgets/templates/{templateId}
   * Updates an existing widget template.
   */
  updateTemplate: async (templateId, templateData) => {
     if (!templateId) return { isError: true, message: "Template ID required.", status: 400 };
    try {
      const { data } = await apiClient.put(
        `${API_URL}/api/v1/widgets/templates/${templateId}`,
        templateData
      );
      return data;
    } catch (err)      {
      return handleApiError(err, `updating template ${templateId}`);
    }
  },

  /**
   * DELETE /api/v1/widgets/templates/{templateId}
   * Deletes a widget template.
   */
  deleteTemplate: async (templateId) => {
     if (!templateId) return { isError: true, message: "Template ID required.", status: 400 };
    try {
      await apiClient.delete(
        `${API_URL}/api/v1/widgets/templates/${templateId}`
      );
      return { success: true };
    } catch (err) {
      return handleApiError(err, `deleting template ${templateId}`);
    }
  },

  /**
   * GET /api/v1/widgets/templates/{template_id}/allowed-origins
   * Fetches the list of allowed origin hostnames for a template.
   */
  getTemplateAllowedOrigins: async (templateId) => {
    if (!templateId) {
      return { isError: true, message: 'Template ID is required.', status: 400 };
    }
    try {
      const { data } = await apiClient.get(
        `${API_URL}/api/v1/widgets/templates/${templateId}/allowed-origins`
      );
      return Array.isArray(data) ? data : [];
    } catch (err) {
       if (err.response?.status === 404) {
           console.warn(`No allowed origins found for template ${templateId} (or template not found). Returning empty list.`);
           return [];
       }
      return handleApiError(err, `fetching allowed origins for template ${templateId}`);
    }
  },

  /**
   * PUT /api/v1/widgets/templates/{template_id}/allowed-origins
   * Replaces the list of allowed origin hostnames for a template.
   */
  updateTemplateAllowedOrigins: async (templateId, originsList) => {
    if (!templateId) {
      return { isError: true, message: 'Template ID is required.', status: 400 };
    }
    if (!Array.isArray(originsList)) {
        return { isError: true, message: 'Origins list must be an array.', status: 400 };
    }
    try {
      const { data } = await apiClient.put(
        `${API_URL}/api/v1/widgets/templates/${templateId}/allowed-origins`,
        originsList
      );
      return Array.isArray(data) ? data : [];
    } catch (err) {
      return handleApiError(err, `updating allowed origins for template ${templateId}`);
    }
  },
};